import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { resizeImage } from "../../../services/screenshot.service"
import { useToitEnPente } from "../../../store/toit-en-pente/hooks"
import { useModal } from "../../../store/modal/hooks"
import { renderer } from "../../viewer/Renderer"
import { useApplication } from "../../../store/application/hooks"
import { isMobile } from "../../../services/helpers.service"
import {
  ToitEnPenteClimateTypes,
  ToitEnPenteInsulationTypes,
  ToitEnPenteRainCoverTypes,
  ToitEnPenteStructureTypes,
} from "@iko-design-center/shared"
import { useAccountState } from "../../providers/AccountStateProvider"
import { useQueryParams } from "../../../store/query/toit-en-pente.hooks"
import { ToitEnPenteData } from "../../../store/toit-en-pente/store"
import { Models } from "../../viewer/renderer.api"
import { ApplicationTypes } from "../../../store/application/store"

type VisibleState = {
  toitType: boolean
  toitRoofSlats: boolean
  toitVolige: boolean
  toitVaporBarier: boolean
  toitInsulation: boolean
  toitInsulationFoil: boolean
  toitRainCover: boolean
  toitConstruction: boolean
  details: boolean
  // SKYLIGHT
  skylight: boolean
  skylightGutter: boolean
  skylightAccessories: boolean
  // CHIMNEY
  chimneyTransitPan: boolean
  chimneyDrain: boolean
  // FOOTING BEAM
  footingBeamGutter: boolean
  // VALLEY GUTTER
  valleyGutter: boolean
  valleyGutterCarpentry: boolean
  valleyGutterGutter: boolean
  // RIDGE
  ridgeSlab: boolean
  ridgeBeam: boolean
}

export function useOverview() {
  const account = useAccountState()
  const { t, i18n } = useTranslation() as any
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const {
    configurationState: {
      toitClimate,
      toitStructure,
      toitRainCover,
      toitInsulation,
      toitAcousticLayer,
    },
    thermalPerformance,
    setOverviewScreenshot,
    setHumanReadableName,
    setToitInsulationTypeWithThickness: setToitInsulationTypeWithThicknessState,
    setToitClimateType: setToitClimateTypeState,
  } = useToitEnPente()

  const toitClimateType = toitClimate.type!
  const toitStructureType = toitStructure.type!
  const toitRainCoverType = toitRainCover.type!
  const toitInsulationType = toitInsulation.type!
  const toitAcousticLayerType = toitAcousticLayer.type!

  const {
    displayToitEnPenteDownloadModal,
    displayToitEnPenteThermalCalculationModal,
    displayRequestAdviceModal,
    displaySaveConfigurationModal,
    displaySaveConfigurationSuccessModal,
  } = useModal()
  const {
    currentModel,
    applicationType,
    directLink,
    setDirectLink,
  } = useApplication()
  const { setQueryParams } = useQueryParams()

  useEffect(() => {
    renderer.displayToitEnPenteStructureType(toitStructureType)
  }, [currentModel, toitStructureType])

  useEffect(() => {
    const localMaterial = { ...toitInsulation.material }
    const presetThickness = localMaterial.thickness

    setToitClimateTypeState(ToitEnPenteClimateTypes.PLAIN_CLIMATE)
    if (presetThickness) {
      setToitInsulationTypeWithThicknessState(
        ToitEnPenteInsulationTypes.ALU_NF_AS,
        presetThickness.toString()
      )
    }
    // eslint-disable-next-line
  }, [setToitInsulationTypeWithThicknessState, setToitClimateTypeState])

  const initialVisibleState = useMemo(
    () => ({
      toitType: true,
      toitRoofSlats: true,
      toitVolige: true,
      toitVaporBarier: true,
      toitInsulation: true,
      toitInsulationFoil: true,
      toitRainCover: true,
      toitConstruction: true,
      details: true,
      // SKYLIGHT
      skylight: true,
      skylightGutter: true,
      skylightAccessories: true,
      // CHIMNEY
      chimneyTransitPan: true,
      chimneyDrain: true,
      // FOOTING BEAM
      footingBeamGutter: true,
      // VALLEY GUTTER
      valleyGutter: true,
      valleyGutterCarpentry: true,
      valleyGutterGutter: true,
      // RIDGE
      ridgeSlab: true,
      ridgeBeam: true,
    }),
    []
  )
  const [visibleState, setVisibleState] = useState<VisibleState>(
    initialVisibleState
  )
  let humanReadableName = `${toitInsulation.type}`

  useEffect(() => {
    setHumanReadableName(humanReadableName)
  }, [humanReadableName, setHumanReadableName])

  useEffect(() => {
    if (!directLink) {
      const data: ToitEnPenteData = {
        toitClimate,
        toitStructure,
        toitRainCover,
        toitInsulation,
        toitAcousticLayer,
        thermalPerformance,
      }
      setQueryParams(data, i18n.language)
    }

    // Handle direct link
    if (renderer && currentModel === Models.DOLLHOUSE && directLink) {
      setDirectLink(false)
      setTimeout(() => {
        let model = Models.TOIT_EN_PENTE
        renderer.displayModel(model).then(() => {
          renderer.displayToitEnPenteInsulationType(toitInsulationType)
          renderer.toggleLayer("ISOLATIE", true)
          renderer.displayToitEnPenteRainCoverType(toitRainCoverType)
          renderer.toggleLayer(
            "ISOLATIE_ONDERDAKFOLIE",
            toitRainCoverType === ToitEnPenteRainCoverTypes.RAIN_COVER_PRESENT
          )
          renderer.displayToitEnPenteStructureType(toitStructureType)
          renderer.toggleLayer(
            "HOUTEN_PLANKEN",
            toitStructureType === ToitEnPenteStructureTypes.ON_WOOD_BOARDS
          )
        })
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    thermalPerformance,
    toitInsulation,
    toitClimate,
    toitStructure,
    toitRainCover,
    toitAcousticLayer,
    setQueryParams,
    directLink,
  ])

  useEffect(
    () => {
      if (!isMobile()) renderer.displayMarkers()
      const dataURL = renderer.createScreenshot()
      resizeImage(dataURL, 520, 420).then((resizedDataURL) => {
        setOverviewScreenshot(resizedDataURL)
      })

      return () => {
        renderer.hideMarkers()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOverviewScreenshot]
  )

  function toggleVisibleState(key: keyof VisibleState) {
    setVisibleState({ ...visibleState, [key]: !visibleState[key] })
  }

  useEffect(() => {
    setVisibleState(initialVisibleState)
    if (!isMobile()) renderer.displayMarkers()
    renderer.toggleAllLayers(applicationType, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModel, setVisibleState, initialVisibleState, applicationType])

  function handleToggleClick(toggle = "toitInsulation", layer = "ISOLATIE") {
    toggleVisibleState(toggle as keyof VisibleState)
    renderer.toggleLayers(layer)
  }

  function handleDetailsClick() {
    toggleVisibleState("details")
    renderer.toggleLayers("DAKDETAILS")
    renderer.toggleDetails()
  }

  function handleAdjustSpecificationsClick() {
    displayToitEnPenteThermalCalculationModal()
  }

  function handleDownloadFilesClick() {
    displayToitEnPenteDownloadModal()
  }

  function handleRequestAdviceClick() {
    displayRequestAdviceModal()
  }

  const copyShareLink = () => {
    const link = window.location.href
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => setLinkCopied(true))
    } else {
      window.prompt(t("flatRoof.overview.shareConfiguration"), link)
      setLinkCopied(true)
    }
  }

  function handleSaveConfigurationClick() {
    if (!account.isAuthenticated) return

    displaySaveConfigurationModal(async (name: string) => {
      const toitEnPenteData: ToitEnPenteData = {
        toitInsulation: toitInsulation,
        toitClimate: toitClimate,
        toitStructure: toitStructure,
        toitRainCover: toitRainCover,
        toitAcousticLayer: toitAcousticLayer,
        thermalPerformance: thermalPerformance,
      }
      const toitEnPenteConfiguration = {
        application_type: ApplicationTypes.TOIT_EN_PENTE,
        application_data: toitEnPenteData,
        display_name: name ?? `${t("home.toitEnPente")}`,
        language: i18n.language,
      }
      account
        .saveConfiguration(toitEnPenteConfiguration)
        .then((response) => {
          displaySaveConfigurationSuccessModal(
            response?.saved_configuration?.record_id
          )
        })
        .catch(() => {
          console.error("Failed to save configuration")
        })
    })
  }

  return {
    t,
    currentModel,
    thermalPerformance,
    visibleState,
    linkCopied,

    toitClimateType,
    toitStructureType,
    toitRainCoverType,
    toitInsulationType,
    toitAcousticLayerType,

    handleToggleClick,
    handleDetailsClick,

    handleAdjustSpecificationsClick,
    handleDownloadFilesClick,
    handleRequestAdviceClick,

    copyShareLink,
    handleSaveConfigurationClick,
  }
}
